/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * UI domain - Skill buttons component for boost and mine abilities
 * skill-buttons.ui.tsx - React component for skill button UI with cooldown visualization
 */

import { BOOST_COOLDOWN, MINE_COOLDOWN } from "@game/config";
import { useGameState } from "@game/state";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { triggerBoost } from "../input/input.bridge";
import { handleMineDeployment } from "../mines/mines";

/**
 * Detect if device is mobile (touch-primary) vs desktop
 */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const hasTouch = window.matchMedia(
        "(hover: none) and (pointer: coarse)",
      ).matches;
      setIsMobile(hasTouch);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
}

/**
 * Calculate cooldown progress from 0 (just used) to 1 (ready)
 * @param lastUsedTimestamp - Unix timestamp when skill was last used
 * @param cooldownMs - Cooldown duration in milliseconds
 * @returns Progress from 0 to 1, clamped
 */
function calculateCooldownProgress(
  lastUsedTimestamp: number,
  cooldownMs: number,
): number {
  // Handle edge case: never used (timestamp is 0)
  if (lastUsedTimestamp === 0) {
    return 1.0;
  }

  const now = Date.now();
  const timeSinceLastUse = now - lastUsedTimestamp;

  // Clamp progress between 0 and 1
  const progress = Math.min(timeSinceLastUse / cooldownMs, 1.0);

  return Math.max(progress, 0.0);
}

const BOOST_COOLDOWN_MS = BOOST_COOLDOWN * 1000;
const MINE_COOLDOWN_MS = MINE_COOLDOWN * 1000;

function getContainerStyle(isMobile: boolean): React.CSSProperties {
  if (isMobile) {
    return {
      position: "fixed",
      bottom: "30px",
      left: "0",
      right: "0",
      display: "flex",
      justifyContent: "space-between",
      padding: "0 200px",
      pointerEvents: "none",
      zIndex: 100,
    };
  }

  return {
    position: "fixed",
    bottom: "180px",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    gap: "20px",
    pointerEvents: "auto",
    zIndex: 100,
  };
}

function getKeyLabelStyle(isMobile: boolean): React.CSSProperties {
  return {
    position: "absolute",
    top: isMobile ? "-20px" : "-25px",
    left: "50%",
    transform: "translateX(-50%)",
    fontFamily: "monospace",
    fontSize: isMobile ? "8px" : "10px",
    color: isMobile ? "rgba(200, 200, 200, 0.6)" : "rgba(200, 200, 200, 0.9)",
    textTransform: "uppercase",
    letterSpacing: "1px",
    whiteSpace: "nowrap",
    display: isMobile ? "none" : "block",
  };
}

function getButtonStyle(
  isReady: boolean,
  isMobile: boolean,
): React.CSSProperties {
  const size = isMobile ? "60px" : "100px";
  const bgOpacity = isMobile ? 0.4 : 0.9;
  const borderOpacity = isReady ? (isMobile ? 0.5 : 0.8) : isMobile ? 0.3 : 0.8;
  const buttonOpacity = isReady ? (isMobile ? 0.7 : 1) : isMobile ? 0.4 : 0.6;

  return {
    position: "relative",
    width: size,
    height: size,
    backgroundColor: `rgba(30, 30, 30, ${bgOpacity})`,
    border: `2px solid ${isReady ? `rgba(100, 200, 255, ${borderOpacity})` : `rgba(60, 60, 60, ${borderOpacity})`}`,
    cursor: isReady ? "pointer" : "not-allowed",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: buttonOpacity,
    transition: "opacity 0.2s ease",
    pointerEvents: "auto",
  };
}

function getCooldownOverlayStyle(
  progress: number,
): React.CSSProperties | undefined {
  if (progress >= 1) {
    return undefined;
  }

  const fillHeight = (1 - progress) * 100;

  return {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: `${fillHeight}%`,
    backgroundColor: "rgba(0, 0, 0, 0.85)",
    borderTop: "2px solid rgba(100, 200, 255, 0.6)",
    pointerEvents: "none",
    zIndex: 10,
  };
}

const BoostIcon: React.FC<{ isReady: boolean; isMobile: boolean }> = ({
  isReady,
  isMobile,
}) => {
  const strokeColor = isReady
    ? isMobile
      ? "rgba(100, 200, 255, 0.7)"
      : "rgba(100, 200, 255, 0.9)"
    : isMobile
      ? "rgba(80, 80, 80, 0.4)"
      : "rgba(80, 80, 80, 0.6)";
  const size = isMobile ? 30 : 40;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      style={{ pointerEvents: "none" }}
      aria-label="Boost icon"
      role="img"
    >
      <path
        d="M 20 5 L 10 25 L 15 30 L 20 25 L 25 30 L 30 25 Z"
        fill="none"
        stroke={strokeColor}
        strokeWidth="2"
      />
      <path
        d="M 20 10 L 14 22 L 20 25 L 26 22 Z"
        fill="none"
        stroke={strokeColor}
        strokeWidth="2"
      />
    </svg>
  );
};

const MineIcon: React.FC<{ isReady: boolean; isMobile: boolean }> = ({
  isReady,
  isMobile,
}) => {
  const color = isReady
    ? isMobile
      ? "rgba(150, 150, 150, 0.7)"
      : "rgba(150, 150, 150, 0.9)"
    : isMobile
      ? "rgba(80, 80, 80, 0.4)"
      : "rgba(80, 80, 80, 0.6)";
  const size = isMobile ? 30 : 40;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      style={{ pointerEvents: "none" }}
      aria-label="Mine icon"
      role="img"
    >
      <circle
        cx="20"
        cy="20"
        r="12"
        fill="none"
        stroke={color}
        strokeWidth="2"
      />
      <circle cx="20" cy="20" r="6" fill={color} />
      {Array.from({ length: 8 }).map((_, i) => {
        const angle = (i / 8) * Math.PI * 2;
        const spikeStartX = 20 + Math.cos(angle) * 12;
        const spikeStartY = 20 + Math.sin(angle) * 12;
        const spikeEndX = 20 + Math.cos(angle) * 17;
        const spikeEndY = 20 + Math.sin(angle) * 17;

        return (
          <line
            // biome-ignore lint/suspicious/noArrayIndexKey: Static array, never reorders
            key={`spike-${i}`}
            x1={spikeStartX}
            y1={spikeStartY}
            x2={spikeEndX}
            y2={spikeEndY}
            stroke={color}
            strokeWidth="2"
          />
        );
      })}
    </svg>
  );
};

export const SkillButtons: React.FC = () => {
  const { localPlayer, gameStarted } = useGameState((state) => ({
    localPlayer: state.playerId ? state.players.get(state.playerId) : null,
    gameStarted: state.gameStarted,
  }));

  const isMobile = useIsMobile();

  const [boostCooldownProgress, setBoostCooldownProgress] = useState(1.0);
  const [mineCooldownProgress, setMineCooldownProgress] = useState(1.0);

  // Use ref to access latest player state without triggering effect re-runs
  const localPlayerRef = useRef(localPlayer);
  localPlayerRef.current = localPlayer;

  // biome-ignore lint/correctness/useExhaustiveDependencies: localPlayer needed to restart animation on timestamp changes
  useEffect(() => {
    const player = localPlayerRef.current;
    if (!player || !gameStarted || player.health <= 0) {
      return;
    }

    let rafId: number;

    const updateCooldowns = () => {
      const currentPlayer = localPlayerRef.current;
      if (!currentPlayer) {
        return;
      }

      const boostProgress = calculateCooldownProgress(
        currentPlayer.lastBoostAt,
        BOOST_COOLDOWN_MS,
      );
      const mineProgress = calculateCooldownProgress(
        currentPlayer.lastMineDeployAt,
        MINE_COOLDOWN_MS,
      );

      setBoostCooldownProgress(boostProgress);
      setMineCooldownProgress(mineProgress);

      // Continue animation only if at least one cooldown is active
      if (boostProgress < 1.0 || mineProgress < 1.0) {
        rafId = requestAnimationFrame(updateCooldowns);
      }
    };

    rafId = requestAnimationFrame(updateCooldowns);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [gameStarted, localPlayer]);

  if (!localPlayer || !gameStarted || localPlayer.health <= 0) {
    return null;
  }

  const boostIsReady = boostCooldownProgress >= 1;
  const mineIsReady = mineCooldownProgress >= 1;

  const handleBoostClick = () => {
    if (boostIsReady) {
      triggerBoost();
    }
  };

  const handleMineClick = () => {
    if (mineIsReady) {
      handleMineDeployment();
    }
  };

  return (
    <div style={getContainerStyle(isMobile)}>
      <button
        type="button"
        style={getButtonStyle(boostIsReady, isMobile)}
        disabled={!boostIsReady}
        onClick={handleBoostClick}
        aria-label="Boost ability"
      >
        <div style={getKeyLabelStyle(isMobile)}>SPACE</div>
        <BoostIcon isReady={boostIsReady} isMobile={isMobile} />
        {!boostIsReady && (
          <>
            <div style={getCooldownOverlayStyle(boostCooldownProgress)} />
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                color: "rgba(100, 200, 255, 0.9)",
                fontSize: isMobile ? "20px" : "28px",
                fontWeight: "bold",
                fontFamily: "monospace",
                zIndex: 11,
                pointerEvents: "none",
              }}
            >
              {((1 - boostCooldownProgress) * BOOST_COOLDOWN).toFixed(1)}
            </div>
          </>
        )}
      </button>

      <button
        type="button"
        style={getButtonStyle(mineIsReady, isMobile)}
        disabled={!mineIsReady}
        onClick={handleMineClick}
        aria-label="Mine deploy ability"
      >
        <div style={getKeyLabelStyle(isMobile)}>RIGHT CLICK</div>
        <MineIcon isReady={mineIsReady} isMobile={isMobile} />
        {!mineIsReady && (
          <>
            <div style={getCooldownOverlayStyle(mineCooldownProgress)} />
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                color: "rgba(150, 150, 150, 0.9)",
                fontSize: isMobile ? "20px" : "28px",
                fontWeight: "bold",
                fontFamily: "monospace",
                zIndex: 11,
                pointerEvents: "none",
              }}
            >
              {((1 - mineCooldownProgress) * MINE_COOLDOWN).toFixed(1)}
            </div>
          </>
        )}
      </button>
    </div>
  );
};
